/* eslint-disable no-param-reassign */
/**
 * @Author: Roni Laukkarinen
 * @Date:   2021-09-01 11:55:37
 * @Last Modified by:   Roni Laukkarinen
 * @Last Modified time: 2022-10-31 16:23:45
 */
/**
 * Style external links
 */

import getLocalization from './localization';

function isLinkExternal(link, localDomains) {
  // Empty links are not external
  if (!link.length) {
    return false;
  }

  const exceptions = [
    '#',
    'tel:',
    'mailto:',
    '/',
    'https://facebook.com',
    'https://www.facebook.com',
    'https://instagram.com',
    'https://www.instagram.com',
    'https://linkedin.com',
    'https://www.linkedin.com',
    'https://twitter.com',
    'https://www.twitter.com',
    'https://x.com',
    'https://www.x.com',
    'https://youtube.com',
    'https://www.youtube.com',
  ];

  // Check if the url starts with some of the exceptions
  const isException = exceptions.some((exception) => {
    const compare = new RegExp(`^${exception}`, 'g');
    return compare.test(link);
  });

  if (isException) {
    return false;
  }

  let linkUrl;
  try {
    linkUrl = new URL(link);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(`Invalid URL: ${link}`);
    return false;
  }
  // Check if host is one of the local domains
  return !localDomains.some((domain) => linkUrl.host === domain);
}

/**
  * Try to get image alt texts from inside a link
  * to use in aria-label, when only elements inside
  * of link are images
  * @param {*} link DOM link element
  * @returns string
  */
export function getChildAltText(link) {
  const children = [...link.children];

  if (children.length === 0) {
    return '';
  }

  const childImgs = children.filter((child) => child.tagName.toLowerCase() === 'img');

  // If there are other elements than img elements, no need to add aria-label
  if (children.length !== childImgs.length) {
    return '';
  }

  // Find alt texts and add to array
  const altTexts = childImgs.filter((child) => child.alt && child.alt !== '').map((child) => child.alt);

  // If there is no alt texts,
  if (!altTexts.length) {
    return '';
  }

  return altTexts.join(', ');
}

export function styleExternalLinks() {
  let localDomains = [
    window.location.host,
  ];

  if (typeof window.air_light_externalLinkDomains !== 'undefined') {
    localDomains = localDomains.concat(window.air_light_externalLinkDomains);
  }

  const links = document.querySelectorAll('a');

  const externalLinks = [...links].filter((link) => isLinkExternal(link.href, localDomains));

  // eslint-disable-next-line consistent-return
  externalLinks.forEach((externalLink) => {
    // Abort mission if there is only img element inside of link
    if (externalLink.childElementCount === 1 && externalLink.children[0].tagName.toLowerCase() === 'img') {
      return false;
    }

    if (!externalLink.classList.contains('no-external-link-label')) {
      const textContent = externalLink.textContent.trim().length
        ? externalLink.textContent.trim() : getChildAltText(externalLink);
      const ariaLabel = externalLink.target === '_blank' ? `${textContent}: ${getLocalization('external_link')}, ${getLocalization('target_blank')}` : `${textContent}: ${getLocalization('external_link')}`;
      externalLink.setAttribute('aria-label', ariaLabel);
    }

    // Arrow icon won't be added if one of these classes is defined for the link
    const classExceptions = [
      'no-external-link-indicator',
      'global-link',
      'button',
    ];

    if (!classExceptions.some((className) => externalLink.classList.contains(className))) {
      // Add SVG arrow icon
      externalLink.insertAdjacentHTML('beforeend', '<svg class="external-link-icon" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.6667 6.41667C11.512 6.41667 11.3636 6.47813 11.2542 6.58752C11.1448 6.69692 11.0833 6.84529 11.0833 7V10.5C11.0833 10.6547 11.0219 10.8031 10.9125 10.9125C10.8031 11.0219 10.6547 11.0833 10.5 11.0833H3.5C3.34529 11.0833 3.19692 11.0219 3.08752 10.9125C2.97813 10.8031 2.91667 10.6547 2.91667 10.5V3.5C2.91667 3.34529 2.97813 3.19692 3.08752 3.08752C3.19692 2.97813 3.34529 2.91667 3.5 2.91667H7C7.15471 2.91667 7.30308 2.85521 7.41248 2.74581C7.52188 2.63642 7.58333 2.48804 7.58333 2.33333C7.58333 2.17862 7.52188 2.03025 7.41248 1.92085C7.30308 1.81146 7.15471 1.75 7 1.75H3.5C3.03587 1.75 2.59075 1.93437 2.26256 2.26256C1.93437 2.59075 1.75 3.03587 1.75 3.5V10.5C1.75 10.9641 1.93437 11.4092 2.26256 11.7374C2.59075 12.0656 3.03587 12.25 3.5 12.25H10.5C10.9641 12.25 11.4092 12.0656 11.7374 11.7374C12.0656 11.4092 12.25 10.9641 12.25 10.5V7C12.25 6.84529 12.1885 6.69692 12.0791 6.58752C11.9698 6.47813 11.8214 6.41667 11.6667 6.41667Z" fill="currentColor"/><path d="M9.33216 2.91667H10.2538L6.58466 6.58C6.52998 6.63423 6.48659 6.69874 6.45697 6.76983C6.42736 6.84091 6.41211 6.91716 6.41211 6.99417C6.41211 7.07117 6.42736 7.14742 6.45697 7.2185C6.48659 7.28959 6.52998 7.3541 6.58466 7.40833C6.63889 7.46301 6.7034 7.5064 6.77449 7.53602C6.84557 7.56563 6.92182 7.58088 6.99883 7.58088C7.07583 7.58088 7.15208 7.56563 7.22316 7.53602C7.29425 7.5064 7.35876 7.46301 7.41299 7.40833L11.0822 3.745V4.66667C11.0822 4.82138 11.1436 4.96975 11.253 5.07915C11.3624 5.18854 11.5108 5.25 11.6655 5.25C11.8202 5.25 11.9686 5.18854 12.078 5.07915C12.1874 4.96975 12.2488 4.82138 12.2488 4.66667V2.33333C12.2488 2.17862 12.1874 2.03025 12.078 1.92085C11.9686 1.81146 11.8202 1.75 11.6655 1.75H9.33216C9.17745 1.75 9.02908 1.81146 8.91968 1.92085C8.81028 2.03025 8.74882 2.17862 8.74882 2.33333C8.74882 2.48804 8.81028 2.63642 8.91968 2.74581C9.02908 2.85521 9.17745 2.91667 9.33216 2.91667Z" fill="currentColor"/></svg>');
    }
  });
}

export function initExternalLinkLabels() {
  // Add aria-labels to links without text or aria-labels and contain image with alt text
  const links = [...document.querySelectorAll('a')];
  // eslint-disable-next-line no-unused-vars
  const linksWithImgChildren = links.forEach((link) => {
    // If link already has text content or an aria label no need to add aria-label
    if (link.textContent.trim() !== '' || link.ariaLabel) {
      return;
    }

    const ariaLabel = getChildAltText(link);
    if (ariaLabel !== '') {
      link.ariaLabel = ariaLabel;
    }
  });
}
